import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, Output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { IdentificationDocumentType } from '@core';
import { SelectOption } from '@core/models/common.interface';
import { getDOBFromID } from '@shared/utils/date.util';
import { InputValidators, LookupValidators } from '@shared/utils/form_validators.util';
import { Country, CountrySelectComponent } from '@wlucha/ng-country-select';

@Component({
  selector: 'app-identification-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatNativeDateModule,
    MatDatepickerModule,
    CountrySelectComponent,
    MatInput,
    MatIcon,
  ],
  templateUrl: './identification-form.html',
  styleUrls: ['./identification-form.scss'],
  standalone: true,
})
export class IdentificationForm {
  @Output() formChange = new EventEmitter<FormGroup>();
  @Output() lookupTypeChange = new EventEmitter<string>();

  options: SelectOption[] = [
    { text: 'SA ID Number', value: 'I' },
    { text: 'Passport Number', value: 'P' },
    { text: 'Asylum Document', value: 'A' },
  ];

  lookupType = signal<string>(this.options[0].text);
  lookupTypeSelected = signal<string>('I');

  southAfricanIdForm = signal<FormGroup | undefined>(undefined);
  passportForm = signal<FormGroup | undefined>(undefined);
  asylumForm = signal<FormGroup | undefined>(undefined);

  constructor(private fb: FormBuilder) {
    this.setupFormEffect();
  }

  setupFormEffect() {
    effect(() => {
      const type = this.lookupType();
      const selectedValue = this.options.find((opt) => opt.text === type)?.value;
      if (selectedValue) {
        this.resetFromSelection(selectedValue);
      }
      this.lookupTypeChange.emit(selectedValue ?? '');
    });
  }

  resetFromSelection(lookupType: string) {
    this.southAfricanIdForm.set(undefined);
    this.passportForm.set(undefined);
    this.asylumForm.set(undefined);

    this.setupSelectedForm(lookupType);
  }

  setupSelectedForm(lookupType: string) {
    this.lookupTypeSelected.set(lookupType);
    switch (lookupType) {
      case 'I':
        this.setupSouthAfricanIdForm();
        break;
      case 'P':
        this.setupPassportForm();
        break;
      case 'A':
        this.setupAsylumForm();
        break;
    }
  }

  setSelectedCountry(type: string, country: Country) {
    if (!country?.alpha2) return;

    const form = type === 'P' ? this.passportForm() : this.asylumForm();
    if (form) {
      form.patchValue({ country: country.alpha2.toUpperCase() });
    }
  }

  setDOBFromID() {
    const form = this.southAfricanIdForm();

    if (form?.get('idNumber')?.value.length === 13) {
      const dob = getDOBFromID(form.get('idNumber')?.value);

      if (!isNaN(dob.getTime())) {
        form.patchValue({ dob: dob.toISOString().split('T')[0] });
      }
    }
  }

  setupSouthAfricanIdForm() {
    const form = this.fb.group(
      {
        type: [IdentificationDocumentType.Id],
        idNumber: [
          '',
          [
            Validators.required,
            Validators.maxLength(13),
            Validators.minLength(13),
            InputValidators.numbersOnlyValidator(),
          ],
        ],
        country: ['ZA', Validators.required],
        dob: ['', [Validators.required]],
      },
      { validators: LookupValidators.southAfricanIdValidation('idNumber') }
    );

    this.southAfricanIdForm.set(form);
    this.formChange.emit(this.southAfricanIdForm());
  }

  setupPassportForm() {
    const form = this.fb.group(
      {
        type: [IdentificationDocumentType.Passport],
        passportNumber: [
          '',
          [Validators.required, InputValidators.alphaNumericalInputOnlyValidator()],
        ],
        country: ['', Validators.required],
        expiryDate: ['', [Validators.required, LookupValidators.futureDateValidator()]],
        dob: ['', [Validators.required, LookupValidators.adultValidator()]],
      },
      {
        validators: LookupValidators.passportAndAsylumValidation('country', 'passportNumber', true),
      }
    );

    this.passportForm.set(form);
    this.formChange.emit(this.passportForm());
  }

  setupAsylumForm() {
    const form = this.fb.group(
      {
        type: [IdentificationDocumentType.Asylum],
        asylumNumber: [
          '',
          [Validators.required, InputValidators.alphaNumericalInputOnlyValidator()],
        ],
        country: ['', Validators.required],
        expiryDate: ['', [Validators.required, LookupValidators.futureDateValidator()]],
        dob: ['', [Validators.required, LookupValidators.adultValidator()]],
      },
      {
        validators: LookupValidators.passportAndAsylumValidation('country', 'asylumNumber', false),
      }
    );

    this.asylumForm.set(form);
    this.formChange.emit(this.asylumForm());
  }

  formatDate(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    if (rawValue.length === 4 && event.key !== 'Backspace') {
      input.value = `${rawValue.substring(0, 4)}/`;
    } else if (rawValue.length === 7 && event.key !== 'Backspace') {
      input.value = `${rawValue.substring(0, 4)}/${rawValue.substring(5, 7)}/`;
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    this.lookupType.set(event.option.value);
  }
}
